import { useMemo } from "react";
import { Link, useSearchParams } from "react-router";
import { useCommunityMembers } from "@/hooks/use-community";
import { ArrowLeft, Loader2, Search, ShieldCheck, Sparkles, Users } from "lucide-react";
import { formatDate, formatNumber } from "@/lib/i18n";

const ROLE_OPTIONS = [
  { value: "all", label: "All roles" },
  { value: "admin", label: "Admins" },
  { value: "moderator", label: "Moderators" },
  { value: "member", label: "Members" },
] as const;

const SORT_OPTIONS = [
  { value: "activity", label: "Most active" },
  { value: "newest", label: "Newest" },
  { value: "alpha", label: "Alphabetical" },
] as const;

const ROLE_STYLES: Record<string, string> = {
  admin: "bg-[var(--color-primary)]/10 text-[var(--color-primary)]",
  moderator: "bg-[var(--color-ai)]/10 text-[var(--color-ai)]",
  member: "bg-[var(--color-muted)]/50 text-[var(--color-foreground)]",
};

function getMemberInitials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");
}

export default function CommunityMembersPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get("search") ?? "";
  const role = (searchParams.get("role") as "all" | "admin" | "moderator" | "member" | null) ?? "all";
  const sort = (searchParams.get("sort") as "activity" | "newest" | "alpha" | null) ?? "activity";
  const offset = Number(searchParams.get("offset") ?? "0");

  const query = useMemo(() => ({
    search,
    role,
    sort,
    offset,
    limit: 20,
  }), [offset, role, search, sort]);

  const { members, hasMore, isLoading } = useCommunityMembers(query);

  const updateSearchParams = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([key, value]) => {
      if (!value) {
        next.delete(key);
      } else {
        next.set(key, value);
      }
    });
    if (!patch.offset) {
      next.set("offset", "0");
    }
    setSearchParams(next);
  };

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div className="space-y-3">
        <Link
          to="/community"
          className="inline-flex items-center gap-1 text-sm text-[var(--color-muted-foreground)] transition-colors hover:text-[var(--color-foreground)]"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to community
        </Link>

        <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
          <div className="space-y-2">
            <div className="inline-flex items-center gap-2 rounded-full bg-[var(--color-ai)]/10 px-3 py-1 text-xs font-medium text-[var(--color-ai)]">
              <Sparkles className="h-3.5 w-3.5" />
              Community directory
            </div>
            <div>
              <h1 className="text-3xl font-semibold tracking-tight text-[var(--color-foreground)]">Members</h1>
              <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
                Browse the tenant-wide community directory by activity, newest joiners, or alphabetically. Public profile
                information only.
              </p>
            </div>
          </div>

          <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 py-3 text-xs text-[var(--color-muted-foreground)]">
            Email addresses stay private by default.
          </div>
        </div>
      </div>

      <div className="grid gap-3 rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] p-4 md:grid-cols-[minmax(0,1fr)_180px_180px]">
        <label className="relative flex items-center">
          <Search className="pointer-events-none absolute left-3 h-4 w-4 text-[var(--color-muted-foreground)]" />
          <input
            value={search}
            onChange={(event) => updateSearchParams({ search: event.target.value || null, offset: "0" })}
            placeholder="Search members by name"
            className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] py-2 pl-9 pr-3 text-sm text-[var(--color-foreground)] placeholder:text-[var(--color-muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
          />
        </label>

        <select
          value={role}
          onChange={(event) => updateSearchParams({ role: event.target.value === "all" ? null : event.target.value, offset: "0" })}
          className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
        >
          {ROLE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        <select
          value={sort}
          onChange={(event) => updateSearchParams({ sort: event.target.value, offset: "0" })}
          className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
        >
          {SORT_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-[var(--color-primary)]" />
        </div>
      ) : members.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[var(--color-border)] bg-[var(--color-background)] px-6 py-14 text-center">
          <Users className="mx-auto h-10 w-10 text-[var(--color-muted-foreground)]/40" />
          <p className="mt-3 text-sm text-[var(--color-muted-foreground)]">
            No members matched this search yet.
          </p>
        </div>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {members.map((member) => (
              <article
                key={member.userId}
                className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] p-5 shadow-sm"
              >
                <div className="flex items-start gap-4">
                  {member.avatarUrl ? (
                    <img
                      src={member.avatarUrl}
                      alt=""
                      className="h-12 w-12 rounded-full object-cover"
                    />
                  ) : (
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--color-muted)] text-sm font-semibold text-[var(--color-foreground)]">
                      {getMemberInitials(member.userName)}
                    </div>
                  )}

                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <h2 className="truncate text-base font-semibold text-[var(--color-foreground)]">{member.userName}</h2>
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${ROLE_STYLES[member.role] ?? ROLE_STYLES.member}`}>
                        {member.role}
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
                      Joined {formatDate(member.joinedAt)}
                    </p>
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-3 gap-3 rounded-xl bg-[var(--color-muted)]/20 p-3 text-center">
                  <div>
                    <p className="text-lg font-semibold text-[var(--color-foreground)] tabular-nums">{formatNumber(member.postCount)}</p>
                    <p className="text-[10px] uppercase tracking-wide text-[var(--color-muted-foreground)]">Posts</p>
                  </div>
                  <div>
                    <p className="text-lg font-semibold text-[var(--color-foreground)] tabular-nums">{formatNumber(member.points)}</p>
                    <p className="text-[10px] uppercase tracking-wide text-[var(--color-muted-foreground)]">Points</p>
                  </div>
                  <div>
                    <p className="inline-flex items-center gap-1 text-lg font-semibold text-[var(--color-foreground)] tabular-nums">
                      <ShieldCheck className="h-4 w-4 text-[var(--color-ai)]" />
                      {formatNumber(member.level)}
                    </p>
                    <p className="text-[10px] uppercase tracking-wide text-[var(--color-muted-foreground)]">Level</p>
                  </div>
                </div>
              </article>
            ))}
          </div>

          {hasMore && (
            <div className="flex justify-center">
              <button
                type="button"
                onClick={() => updateSearchParams({ offset: String(offset + 20) })}
                className="rounded-xl border border-[var(--color-border)] px-5 py-2 text-sm text-[var(--color-foreground)] transition-colors hover:bg-[var(--color-muted)]/30"
              >
                Load more
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
